import React, { useState, useCallback, useEffect } from "react";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import MyLocationIcon from "@mui/icons-material/MyLocation";

const libraries: ("places")[] = ["places"];

export interface LocationData {
  latitude: number;
  longitude: number;
  address: string; // Display address (city, state only) - shown publicly
  fullAddress?: string; // Full address - kept private, not stored in DB
}

interface LocationInputProps {
  onLocationSelect: (location: LocationData) => void;
  initialLocation?: LocationData;
  darkMode?: boolean;
}

/**
 * Extracts city and state from Google Maps address components
 * Returns format: "City, ST" (e.g., "Boston, MA")
 */
function extractCityState(addressComponents: google.maps.GeocoderAddressComponent[]): string {
  let city = "";
  let state = "";

  for (const component of addressComponents) {
    const types = component.types;
    
    // City can be locality, sublocality, or administrative_area_level_3
    if (types.includes("locality")) {
      city = component.long_name;
    } else if (!city && types.includes("sublocality_level_1")) {
      city = component.long_name;
    } else if (!city && types.includes("administrative_area_level_3")) {
      city = component.long_name;
    }
    
    // State is administrative_area_level_1
    if (types.includes("administrative_area_level_1")) {
      state = component.short_name; // Use short name for state (e.g., "MA" instead of "Massachusetts")
    }
  }

  if (city && state) {
    return `${city}, ${state}`;
  } else if (city) {
    return city;
  } else if (state) {
    return state;
  }
  
  return "";
}

const FALLBACK_TEST_LOCATION: LocationData = {
  latitude: 42.3601,
  longitude: -71.0589,
  address: "Boston, MA",
};

function getGoogleMapsApiKey(): string {
  return process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
}

/**
 * Shown when REACT_APP_GOOGLE_MAPS_API_KEY is unset — avoids loading a broken
 * Maps script. Offers a one-click Boston fallback so local/CI listing flows
 * (Playwright) can still complete the pricing step.
 */
const LocationInputUnconfigured: React.FC<LocationInputProps> = ({
  onLocationSelect,
  initialLocation,
  darkMode = false,
}) => {
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(
    initialLocation || null
  );

  // EditListing hydrates location after mount; keep the field in sync.
  useEffect(() => {
    if (!initialLocation) return;
    setSelectedLocation(initialLocation);
  }, [
    initialLocation?.address,
    initialLocation?.fullAddress,
    initialLocation?.latitude,
    initialLocation?.longitude,
  ]);

  return (
    <div className="form-group" data-testid="location-input-unconfigured">
      <label htmlFor="location">Location *</label>
      <input
        type="text"
        id="location"
        name="location"
        placeholder={selectedLocation ? selectedLocation.address : "Maps not configured"}
        disabled
        className="form-control wider-input"
        value={selectedLocation?.address || ""}
        readOnly
        style={{
          backgroundColor: darkMode ? "#333" : "#f5f5f5",
          color: darkMode ? "#fff" : "#333",
        }}
      />
      <button
        type="button"
        data-testid="location-use-test-location"
        className="btn btn-outline-primary btn-sm"
        style={{ marginTop: 8 }}
        onClick={() => {
          onLocationSelect(FALLBACK_TEST_LOCATION);
          setSelectedLocation(FALLBACK_TEST_LOCATION);
        }}
      >
        Use Boston, MA (test location)
      </button>
      <div style={{ color: "#dc3545", fontSize: "12px", marginTop: "8px", lineHeight: 1.5 }}>
        Google Maps is not configured. Add <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> to{" "}
        <code>client/.env.local</code> for address autocomplete, or use the test location
        above for local / CI flows.
      </div>
    </div>
  );
};

const LocationInputWithMaps: React.FC<LocationInputProps> = ({
  onLocationSelect,
  initialLocation,
  darkMode = false,
}) => {
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [inputValue, setInputValue] = useState(initialLocation?.fullAddress || initialLocation?.address || "");
  const [displayValue, setDisplayValue] = useState(initialLocation?.address || "");
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // EditListing (and similar) pass location=null on first paint, then hydrate
  // after the query resolves. useState only reads initialLocation on mount, so
  // sync when the parent-provided location identity changes.
  useEffect(() => {
    if (!initialLocation) return;
    setInputValue(initialLocation.fullAddress || initialLocation.address || "");
    setDisplayValue(initialLocation.address || "");
  }, [
    initialLocation?.address,
    initialLocation?.fullAddress,
    initialLocation?.latitude,
    initialLocation?.longitude,
  ]);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: getGoogleMapsApiKey(),
    libraries,
  });

  const onLoad = useCallback((autocompleteInstance: google.maps.places.Autocomplete) => {
    setAutocomplete(autocompleteInstance);
  }, []);

  const onPlaceChanged = useCallback(() => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry?.location) {
        const fullAddress = place.formatted_address || place.name || "";
        
        // Extract city and state for public display
        let cityState = "";
        if (place.address_components) {
          cityState = extractCityState(place.address_components);
        }
        
        // Fallback to formatted address if we couldn't extract city/state
        const displayAddress = cityState || fullAddress;
        
        const locationData: LocationData = {
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
          address: displayAddress, // Public display (city, state)
          fullAddress: fullAddress, // Private (not stored)
        };
        
        setInputValue(fullAddress);
        setDisplayValue(displayAddress);
        setError(null);
        onLocationSelect(locationData);
      }
    }
  }, [autocomplete, onLocationSelect]);

  const handleGetCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser");
      return;
    }

    setIsGettingLocation(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        // Reverse geocode to get address
        try {
          const geocoder = new google.maps.Geocoder();
          const response = await geocoder.geocode({
            location: { lat: latitude, lng: longitude },
          });

          if (response.results[0]) {
            const fullAddress = response.results[0].formatted_address;
            
            // Extract city and state for public display
            let cityState = "";
            if (response.results[0].address_components) {
              cityState = extractCityState(response.results[0].address_components);
            }
            
            const displayAddress = cityState || fullAddress;
            
            const locationData: LocationData = {
              latitude,
              longitude,
              address: displayAddress,
              fullAddress: fullAddress,
            };
            
            setInputValue(fullAddress);
            setDisplayValue(displayAddress);
            onLocationSelect(locationData);
          } else {
            // Use coordinates without address if geocoding fails
            const locationData: LocationData = {
              latitude,
              longitude,
              address: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
            };
            setInputValue(locationData.address);
            setDisplayValue(locationData.address);
            onLocationSelect(locationData);
          }
        } catch (geocodeError) {
          console.error("Geocoding error:", geocodeError);
          const locationData: LocationData = {
            latitude,
            longitude,
            address: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
          };
          setInputValue(locationData.address);
          setDisplayValue(locationData.address);
          onLocationSelect(locationData);
        }

        setIsGettingLocation(false);
      },
      (error) => {
        setIsGettingLocation(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setError("Location permission denied. Please enable location access.");
            break;
          case error.POSITION_UNAVAILABLE:
            setError("Location information is unavailable.");
            break;
          case error.TIMEOUT:
            setError("Location request timed out.");
            break;
          default:
            setError("An unknown error occurred while getting location.");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }, [onLocationSelect]);

  if (loadError) {
    return (
      <div className="form-group">
        <label>Location</label>
        <div style={{ color: "red", lineHeight: 1.5 }}>
          Error loading Google Maps: {loadError.message}
        </div>
        <div style={{ color: "#666", fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
          Common fixes: enable <strong>Maps JavaScript API</strong> and{" "}
          <strong>Places API (Legacy)</strong> in Google Cloud Console, add{" "}
          <code>http://localhost:3000/*</code>, <code>http://127.0.0.1:3000/*</code>,
          and <code>http://localhost:3080/*</code> (Playwright CI) to the key&apos;s
          HTTP referrer allowlist, then restart <code>npm start</code>.
          Open the app at <code>http://localhost:3000</code> (not 127.0.0.1) if uploads
          also fail.
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="form-group">
        <label>Location</label>
        <input
          type="text"
          placeholder="Loading maps..."
          disabled
          className="form-control wider-input"
          style={{
            backgroundColor: darkMode ? "#333" : "#f5f5f5",
            color: darkMode ? "#fff" : "#333",
          }}
        />
      </div>
    );
  }

  return (
    <div className="form-group">
      <label htmlFor="location">Location *</label>
      <div style={{ display: "flex", gap: "8px", alignItems: "stretch" }}>
        <div style={{ flex: 1 }}>
          <Autocomplete
            onLoad={onLoad}
            onPlaceChanged={onPlaceChanged}
            options={{
              types: ["geocode"],
              componentRestrictions: { country: "us" },
            }}
          >
            <input
              type="text"
              id="location"
              name="location"
              data-testid="location-maps-input"
              placeholder="Enter city or address"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className={`form-control wider-input ${darkMode ? 'location-input-dark' : ''}`}
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: "14px",
                border: darkMode ? "1px solid #555" : "1px solid #ccc",
                borderRadius: "4px",
                backgroundColor: darkMode ? "#333" : "#fff",
                color: darkMode ? "#fff" : "#333",
              }}
              required
            />
          </Autocomplete>
        </div>
        <button
          type="button"
          onClick={handleGetCurrentLocation}
          disabled={isGettingLocation}
          title="Use my current location"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "10px 12px",
            backgroundColor: darkMode ? "#444" : "#f0f0f0",
            border: darkMode ? "1px solid #555" : "1px solid #ccc",
            borderRadius: "4px",
            cursor: isGettingLocation ? "wait" : "pointer",
            minWidth: "44px",
          }}
        >
          {isGettingLocation ? (
            <span style={{ fontSize: "12px" }}>...</span>
          ) : (
            <MyLocationIcon style={{ fontSize: "20px", color: darkMode ? "#fff" : "#666" }} />
          )}
        </button>
      </div>
      {error && (
        <div style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>
          {error}
        </div>
      )}
      {displayValue && !error && (
        <div style={{ color: darkMode ? "#aaa" : "#666", fontSize: "12px", marginTop: "4px" }}>
          Will display as: <strong>{displayValue}</strong>
        </div>
      )}
    </div>
  );
};

const LocationInput: React.FC<LocationInputProps> = (props) => {
  if (!getGoogleMapsApiKey()) {
    return <LocationInputUnconfigured {...props} />;
  }
  return <LocationInputWithMaps {...props} />;
};

export default LocationInput;
